import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Alert } from 'react-bootstrap';
import PasswordField from '../components/PasswordField';
import { PASSWORD_MISMATCH_MESSAGE, validatePasswordPair } from '../validation';
import { apiMessage, resetPasswordRequest } from '../api';
import './Login.css';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  // Each password field has its own show/hide state, so revealing one never reveals the other.
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  // Password validation, keyed by field so each message sits under its own input.
  const [fieldErrors, setFieldErrors] = useState({});
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);

  const passwordMismatch = fieldErrors.confirmPassword === PASSWORD_MISMATCH_MESSAGE;

  // Drop a field's message as soon as the user edits it, so it never describes stale input.
  const clearFieldErrors = (...keys) => {
    setFieldErrors((prev) => {
      if (!keys.some((key) => prev[key])) return prev;
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    // A "do not match" message is about the pair, so editing either field retires it.
    clearFieldErrors('password', ...(passwordMismatch ? ['confirmPassword'] : []));
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    clearFieldErrors('confirmPassword');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    if (!token.trim()) {
      setError('Reset token is required');
      return;
    }
    const errors = validatePasswordPair(password, confirmPassword);
    if (errors.password || errors.confirmPassword) {
      setFieldErrors(errors);
      document.getElementById(errors.password ? 'newPassword' : 'confirmPassword')?.focus();
      return;
    }
    setFieldErrors({});
    setLoading(true);
    try {
      const response = await resetPasswordRequest({
        token: token.trim(),
        password,
        confirmPassword,
      });
      setInfo(response.data?.message || 'Password updated. You can sign in with your new password.');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      setError(apiMessage(err, 'Unable to reset password. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <Container fluid className="login-container">
        <Row className="g-0 login-row justify-content-center">
          <Col lg={7} className="signin-panel">
            <div className="signin-content">
              <h2 className="signin-title">Choose a new password</h2>
              <Form onSubmit={handleSubmit} className="signin-form">
                {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
                {info && <Alert variant="success" className="mb-3">{info}</Alert>}
                <Form.Group className="mb-3">
                  <Form.Label className="form-label">Reset token</Form.Label>
                  <Form.Control
                    type="text"
                    id="resetToken"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    required
                    className="form-input"
                    autoComplete="off"
                  />
                </Form.Group>
                {/* On a mismatch both fields are highlighted, but the message belongs to
                    Confirm password; New password points at it for assistive tech. */}
                <PasswordField
                  id="newPassword"
                  label="New password"
                  requiredMark={false}
                  value={password}
                  onChange={handlePasswordChange}
                  show={showPassword}
                  onToggleShow={() => setShowPassword((v) => !v)}
                  error={fieldErrors.password}
                  invalid={Boolean(fieldErrors.password) || passwordMismatch}
                  describedBy={passwordMismatch ? 'confirmPassword-error' : undefined}
                />
                <PasswordField
                  id="confirmPassword"
                  label="Confirm password"
                  requiredMark={false}
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                  show={showConfirmPassword}
                  onToggleShow={() => setShowConfirmPassword((v) => !v)}
                  error={fieldErrors.confirmPassword}
                />
                <button type="submit" className="signin-button" disabled={loading}>
                  {loading ? 'PLEASE WAIT…' : 'UPDATE PASSWORD'}
                  {!loading && <span className="button-arrow">→</span>}
                </button>
              </Form>
              <div className="create-account-section">
                <p className="create-account-text">
                  <button type="button" className="create-account-link" onClick={() => navigate('/login')}>
                    Back to sign in
                  </button>
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ResetPassword;
